import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  DialogContent,
  TextField,
  Button,
  ListItem,
  ListItemText,
  DialogActions,
  CircularProgress,
  List,
  ListItemAvatar,
  Avatar,
  ListItemSecondaryAction,
  Divider,
  Grid,
} from "@material-ui/core";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";
import { makeStyles } from "@material-ui/core/styles";
import {
  Check as CheckIcon,
  Close as XIcon,
  LinkOff as LinkOffIcon,
  Link as LinkIcon,
} from "@material-ui/icons";
import { beep } from "./../../../utils/utils";
import { QrBarcodeScanner } from "../../../components/common/QrBarcodeScanner/QrBarcodeScanner";
import { useNotify } from "react-admin";
import { logError } from "../../../utils/appUtils";

const useStyles = makeStyles((theme) => ({
  iconContainer: {
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
    height: 38,
    width: 38,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    background: "#e0e0e0",
  },
  container: {
    padding: theme.spacing(2),
  },
  closeButton: {
    cursor: "pointer",
  },
  listItem: {
    paddingRight: 0,
    paddingLeft: 0,
  },
  emptyMessage: {
    textAlign: "center",
    marginTop: theme.spacing(2),
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
  primaryColor: {
    color: theme.palette.primary.main,
  },
}));

export const ScanSealsDialog = ({
  onClose,
  setApplySealOpen,
  revalidateSeal,
  inboundShipment,
  dispatch,
  setDeactivatedSeal,
  loading,
  setBreakSealOpen,
}) => {
  const createdBy = window.appUser.fullName;
  const seals = inboundShipment?.seals?.items.filter((s) => s.active) || [];
  const sealsToBeBroken = inboundShipment?.seals?.items.some(
    (s) =>
      s.active &&
      s.reValidated &&
      !s.brokenAt &&
      !s.brokenBy &&
      s.status !== "new"
  );
  const notify = useNotify();
  const isValidating = seals.some((s) => s.status !== "new" && !s.reValidated);
  const styles = useStyles();
  const [sealsError, setSealsError] = useState("");
  const [barcode, setBarcode] = useState("");
  const [isScannerOpen, setIsScannerOpen] = useState(true);
  const ref = useRef();
  const revalidateInFlightRef = useRef(false);

  useEffect(() => {
    if (!isValidating) return undefined;

    const focusTimer = setTimeout(() => {
      ref.current?.focus();
    }, 0);

    return () => clearTimeout(focusTimer);
  }, [isValidating]);

  const onScanSuccess = (v) => {
    if (v !== ref.current?.value) {
      setBarcode(v);
      beep(100, 520, 200);
      setIsScannerOpen(false);
    }
  };

  const onReValidate = async () => {
    if (loading || revalidateInFlightRef.current) return;
    if (!barcode) {
      setSealsError("Required");
      return;
    }

    revalidateInFlightRef.current = true;
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const seal = seals.find((s) => s.barcode === barcode);
      if (!seal) {
        throw Error("Seal not matching");
      }
      await revalidateSeal({
        notify,
        createdBy,
        barcode,
        sealId: seal.id,
        dispatch,
        inboundShipment,
      });
    } catch (error) {
      logError("ScanSealDialog:onReValidate", error, notify);
    } finally {
      revalidateInFlightRef.current = false;
      dispatch({ type: "SET_LOADING", payload: false });
      setBarcode("");
    }
  };

  const applyNewSeal = async (br) => {
    try {
      const seal = seals.find((s) => s.barcode === br);
      if (!seal) {
        throw Error("Seal not matching");
      }
      setDeactivatedSeal({
        barcode: br,
        id: seal.id,
        description: seal.description,
      });
      onClose();
      setApplySealOpen(true);
    } catch (error) {
      logError("ScanSealDialog:applyNewSeal", error, notify);
    } finally {
      setBarcode("");
    }
  };

  const openBreakSeal = () => {
    onClose();
    setBreakSealOpen(true);
  };

  const handleBarcodeInputChange = (e) => {
    if (e.target.value) {
      setSealsError("");
    } else {
      setSealsError("Required");
    }
    setBarcode(e.target.value);
  };

  return (
    <Dialog open={true} fullWidth={true}>
      <Box
        component="span"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        m={2}
      >
        <Typography variant="h6">
          {isValidating ? "Validate Seals" : "Manage Seals"}
        </Typography>
        <CancelOutlinedIcon
          className={styles.closeButton}
          fontSize="large"
          onClick={onClose}
        />
      </Box>
      <DialogContent className={styles.container}>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" mb={2}>
            {!isValidating && sealsToBeBroken && (
              <>
                <Button
                  disabled={loading}
                  variant="contained"
                  onClick={openBreakSeal}
                >
                  Break
                </Button>
              </>
            )}
          </Box>
        </Grid>
        {isValidating && (
          <>
            <Grid item xs={12}>
              {isScannerOpen ? (
                <QrBarcodeScanner onScanSuccess={onScanSuccess} />
              ) : (
                <Box display="flex" justifyContent="center" mb={2}>
                  <Button
                    onClick={() => setIsScannerOpen(true)}
                    color="primary"
                    variant="outlined"
                    margin="dense"
                  >
                    Rescan
                  </Button>
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <Box
                display="flex"
                gridGap={"10px"}
                alignItems="center"
                justifyContent="space-between"
                mt={2}
              >
                <TextField
                  label="Barcode"
                  variant="outlined"
                  fullWidth
                  onChange={handleBarcodeInputChange}
                  margin="dense"
                  value={barcode}
                  error={!!sealsError}
                  helperText={sealsError}
                  inputRef={ref}
                  onKeyDown={(evt) => {
                    if (evt.key === "Enter") {
                      evt.preventDefault();
                      if (loading || revalidateInFlightRef.current) return;
                      onReValidate();
                    }
                  }}
                />
                {barcode && (
                  <Button
                    disabled={loading}
                    variant="contained"
                    onClick={onReValidate}
                  >
                    {loading ? <CircularProgress size={24} /> : "Validate"}
                  </Button>
                )}
              </Box>
            </Grid>
          </>
        )}
        {seals.length === 0 ? (
          <Typography className={styles.emptyMessage}>
            No seals available
          </Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <List>
                {seals.map((seal, idx) => {
                  const {
                    id,
                    barcode,
                    description,
                    reValidated,
                    status,
                    brokenAt,
                    brokenBy,
                  } = seal;
                  const isNew = status === "new";
                  const isBroken = brokenAt && brokenBy && reValidated;
                  const isRevalidated = !brokenAt && !brokenBy && reValidated;
                  const isNotRevalidated =
                    !brokenAt && !brokenBy && !reValidated && status !== "new";
                  return (
                    <React.Fragment key={id}>
                      <ListItem className={styles.listItem}>
                        <ListItemAvatar>
                          <Avatar className={styles.iconContainer}>
                            {isRevalidated && <CheckIcon fontSize="small" />}
                            {isNotRevalidated && <XIcon fontSize="small" />}
                            {isBroken && <LinkOffIcon fontSize="small" />}
                            {isNew && <LinkIcon fontSize="small" />}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={barcode}
                          secondary={description}
                        />
                        <ListItemSecondaryAction>
                          {brokenAt && (
                            <Button
                              disabled={loading}
                              variant="contained"
                              onClick={() => applyNewSeal(barcode)}
                            >
                              {loading ? (
                                <CircularProgress size={24} />
                              ) : (
                                "Replace"
                              )}
                            </Button>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                      {idx < seals.length - 1 ? <Divider /> : null}
                    </React.Fragment>
                  );
                })}
              </List>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
